"use client"

import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Menu, X, ChevronDown, ArrowRight, UserRound } from "lucide-react"
import { cn } from "@/lib/utils"

export interface NavChild {
  label: string
  href: string
  disabled?: boolean
  external?: boolean
}

export interface NavLink {
  label: string
  href: string
  children?: NavChild[]
  wide?: boolean
}

// Existing-page check — routes with active page.tsx files
// User kept only: criminal-record-checks (services), healthcare + restaurant (industries),
// small-business (solutions). All other service/industry/solution pages are disabled.
const EXISTING_PAGES = new Set([
  "/",
  "/pricing",
  "/pass-through-fees",
  "/contact-us",
  "/platform",
  "/sign-up",
  "/about-us",
  "/about-us/pbsa-accreditation",
  "/about-us/careers",
  "/testimonials",
  "/resources/blog",
  "/resources/faq",
  "/resources/videos",
  "/resources/case-studies",
  "/resources/victims-of-human-trafficking",
  "/partners/integrations",
  "/partners/become-a-partner",
  "/paylocity",
  "/terms-and-conditions",
  "/privacy-policy",
  "/cookie-policy",
  "/html-sitemap",
  // Solutions — Small Business + Enterprises + Property Management kept
  "/solution/background-checks-for-small-business",
  "/solution/background-checks-for-enterprises",
  "/solution/background-checks-for-property-management-and-landlords",
  "/solution/background-checks-yourself-see-what-employers-see",
  "/service/consumer-report",
  // Services — only Criminal Record Checks kept
  "/service/criminal-record-checks",
  "/service/social-security-trace",
  "/service/employment-verification-services",
  "/service/reference-checks",
  "/service/identity-verification",
  "/service/current-workforce-monitoring",
  "/service/education-verification-services",
  "/service/social-media-checks",
  "/service/form-i9-e-verify",
  "/service/compliance-services",
  "/service/credit-and-financial-checks",
  "/service/professional-license-verification",
  "/service/government-watchlist-and-sanction-checks",
  "/service/international-and-global-background-checks",
  "/service/smart-screen",
  "/service/civil-record-check",
  "/service/motor-vehicle-checks",
  "/service/healthcare-sanction-checks",
  "/service/wotc-screening",
  "/service/drug-screen-services",
  // Industries — Healthcare, Restaurant, Automotive Dealerships, Construction, Day Care, Energy & Utilities, Financial Services, Franchise, Gig Economy & Marketplace, Government, Higher Education, Hospitality, Insurance, K-12 Education kept
  "/industries/healthcare",
  "/industries/restaurant",
  "/industries/automotive-dealerships",
  "/industries/construction",
  "/industries/day-care",
  "/industries/energy-and-utilities",
  "/industries/financial-services",
  "/industries/franchise",
  "/industries/gig-economy-and-marketplace",
  "/industries/government",
  "/industries/higher-education",
  "/industries/hospitality",
  "/industries/insurance",
  "/industries/k12-education",
  "/industries/manufacturing",
  "/industries/non-profit",
  "/industries/retail",
  "/industries/staffing",
  "/industries/technology",
  "/industries/tenant",
  "/industries/transportation-and-logistics",
])

// Hidden from primary nav — pages that exist but should appear disabled in the menu
const HIDDEN_FROM_NAV = new Set([
  "/resources/videos",
])

function child(label: string, href: string): NavChild {
  return { label, href, disabled: !EXISTING_PAGES.has(href) || HIDDEN_FROM_NAV.has(href) }
}

function externalChild(label: string, href: string): NavChild {
  return { label, href, external: true }
}

const navLinks: NavLink[] = [
  {
    label: "Solutions",
    href: "#",
    children: [
      child("Background Checks for Small Business", "/solution/background-checks-for-small-business"),
      child("Background Checks for Enterprises", "/solution/background-checks-for-enterprises"),
      child("Property Management and Landlords", "/solution/background-checks-for-property-management-and-landlords"),
      child("Check Yourself (See What Employers See)", "/solution/background-checks-yourself-see-what-employers-see"),
      child("ID Verification as a Consumer Report", "/service/consumer-report"),
    ],
  },
  {
    label: "API",
    href: "#",
    children: [
      externalChild("API Documentation", "https://docs.3rddegreescreening.com/docs/getting-started"),
      externalChild("API Reference", "https://docs.3rddegreescreening.com/reference/createvidinvite"),
    ],
  },
  {
    label: "Services",
    href: "#",
    wide: true,
    children: [
      child("Social Security Trace", "/service/social-security-trace"),
      child("Criminal Record Checks", "/service/criminal-record-checks"),
      child("Credit & Financial Checks", "/service/credit-and-financial-checks"),
      child("Civil Record Checks", "/service/civil-record-check"),
      child("Employment Verification", "/service/employment-verification-services"),
      child("Education Verification", "/service/education-verification-services"),
      child("Professional License Verification", "/service/professional-license-verification"),
      child("Motor Vehicle Checks", "/service/motor-vehicle-checks"),
      child("Reference Checks", "/service/reference-checks"),
      child("Social Media Check", "/service/social-media-checks"),
      child("Government WatchList & Sanctions", "/service/government-watchlist-and-sanction-checks"),
      child("Healthcare Sanctions Checks", "/service/healthcare-sanction-checks"),
      child("Identity Verification", "/service/identity-verification"),
      child("Form I9 & E-Verify", "/service/form-i9-e-verify"),
      child("International & Global Checks", "/service/international-and-global-background-checks"),
      child("WOTC Screening", "/service/wotc-screening"),
      child("Current Workforce Monitoring", "/service/current-workforce-monitoring"),
      child("Compliance Services", "/service/compliance-services"),
      child("Smart Screenᵀᴹ", "/service/smart-screen"),
      child("Drug Screening Services", "/service/drug-screen-services"),
    ],
  },
  {
    label: "Industries",
    href: "#",
    wide: true,
    children: [
      child("Automotive Dealerships", "/industries/automotive-dealerships"),
      child("Construction", "/industries/construction"),
      child("Day Care", "/industries/day-care"),
      child("Energy & Utilities", "/industries/energy-and-utilities"),
      child("Financial Services", "/industries/financial-services"),
      child("Franchise", "/industries/franchise"),
      child("Gig Economy & Marketplace", "/industries/gig-economy-and-marketplace"),
      child("Government", "/industries/government"),
      child("Higher Education", "/industries/higher-education"),
      child("Healthcare", "/industries/healthcare"),
      child("Hospitality", "/industries/hospitality"),
      child("Insurance", "/industries/insurance"),
      child("K-12 Education", "/industries/k12-education"),
      child("Manufacturing", "/industries/manufacturing"),
      child("Non-Profit", "/industries/non-profit"),
      child("Restaurant", "/industries/restaurant"),
      child("Retail", "/industries/retail"),
      child("Staffing", "/industries/staffing"),
      child("Technology", "/industries/technology"),
      child("Tenant", "/industries/tenant"),
      child("Transportation & Logistics", "/industries/transportation-and-logistics"),
    ],
  },
  {
    label: "Partners",
    href: "#",
    children: [
      child("Integrations", "/partners/integrations"),
      child("Become a Partner", "/partners/become-a-partner"),
    ],
  },
  {
    label: "Resources",
    href: "#",
    children: [
      child("Blog", "/resources/blog"),
      child("Victims of Human Trafficking", "/resources/victims-of-human-trafficking"),
      child("Case Studies", "/resources/case-studies"),
      child("FAQs", "/resources/faq"),
    ],
  },
  {
    label: "About Us",
    href: "#",
    children: [
      child("About 3rd Degree Screening", "/about-us"),
      child("PBSA Accreditation", "/about-us/pbsa-accreditation"),
      child("Careers", "/about-us/careers"),
      child("What Our Clients Say", "/testimonials"),
    ],
  },
]

export default function Navbar() {
  const [mobileOpen, setMobileOpen]         = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [openSection, setOpenSection]       = useState<string | null>(null)
  const [scrolled, setScrolled]             = useState(false)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // On the "Check Yourself" page, Contact Us scrolls to the in-page form
  // (#contact) instead of navigating to /contact-us. Every other page keeps
  // the standard navigation behavior.
  const pathname = usePathname()
  const isCheckYourselfPage =
    pathname === "/solution/background-checks-yourself-see-what-employers-see"
  const contactHref = isCheckYourselfPage ? "#contact" : "/contact-us"

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20)
    handler()
    window.addEventListener("scroll", handler, { passive: true })
    return () => window.removeEventListener("scroll", handler)
  }, [])

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : ""
    return () => { document.body.style.overflow = "" }
  }, [mobileOpen])

  const toggleSection = (label: string) =>
    setOpenSection((prev) => (prev === label ? null : label))

  return (
    <>
      {/* ── Floating glassmorphic navbar ── */}
      <div className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-5 pt-4 pointer-events-none">
        <div className="mx-auto max-w-7xl pointer-events-auto">
          <header
            className={cn(
              "transition-all duration-300",
              scrolled
                ? "bg-white/95 md:bg-white/90 backdrop-blur-none md:backdrop-blur-2xl shadow-lg shadow-black/8 border border-white/80 rounded-2xl"
                : "bg-white/95 md:bg-white/75 backdrop-blur-none md:backdrop-blur-xl shadow-md shadow-black/5 border border-white/60 rounded-2xl"
            )}
          >
            <div className="px-4 sm:px-6">
              <div className="flex items-center justify-between h-16">

                {/* Logo */}
                <Link href="/" className="flex-shrink-0 relative z-10">
                  <Image
                    src="/images/logo.png"
                    alt="3rd Degree Screening"
                    width={180}
                    height={56}
                    className="h-11 w-auto object-contain"
                    priority
                  />
                </Link>

                {/* Desktop Nav */}
                <nav
                  className="hidden xl:flex items-center gap-0.5 relative"
                  onMouseLeave={() => {
                    leaveTimer.current = setTimeout(() => {
                      setActiveDropdown(null)
                      leaveTimer.current = null
                    }, 200)
                  }}
                >
                  {navLinks.map((link) => (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={() => {
                        if (leaveTimer.current) clearTimeout(leaveTimer.current)
                        if (link.children) setActiveDropdown(link.label)
                      }}
                    >
                      <Link
                        href={link.href}
                        className="flex items-center gap-1 px-3 py-3 -my-1 text-[13.5px] font-medium rounded-xl transition-all duration-150 text-foreground/80 hover:text-[#1A3FD8] hover:bg-[#1A3FD8]/5"
                      >
                        {link.label}
                        {link.children && (
                          <ChevronDown
                            className={cn(
                              "w-3.5 h-3.5 transition-transform duration-200",
                              activeDropdown === link.label ? "rotate-180" : ""
                            )}
                          />
                        )}
                      </Link>
                    </div>
                  ))}

                  {/* Unified Mega Menu Dropdown — rendered once at nav level, centered on the nav pill */}
                  {(() => {
                    const activeLink = activeDropdown
                      ? navLinks.find((l) => l.label === activeDropdown && l.children)
                      : null
                    if (!activeLink || !activeLink.children) return null
                    const count = activeLink.children.length
                    const cols = count <= 4 ? 2 : count <= 12 ? 3 : 4
                    const isWide = count > 12
                    const megaWidth = isWide
                      ? "min(56rem, calc(100vw - 2rem))"
                      : count <= 12
                      ? "min(40rem, calc(100vw - 2rem))"
                      : "min(24rem, calc(100vw - 2rem))"
                    return (
                      <>
                        {/* invisible bridge spanning the entire nav width so the cursor can travel from any trigger to the dropdown */}
                        <div
                          className="absolute top-full left-0 right-0 h-4 pointer-events-auto"
                          onMouseEnter={() => {
                            if (leaveTimer.current) clearTimeout(leaveTimer.current)
                          }}
                        />
                        <div
                          className={cn(
                            "absolute top-[calc(100%+0.75rem)] left-1/2 -translate-x-1/2 bg-white rounded-2xl border border-gray-200/80 shadow-2xl shadow-black/10 transition-all duration-200 origin-top",
                            "opacity-100 scale-100 pointer-events-auto translate-y-0"
                          )}
                          style={{ width: megaWidth }}
                          onMouseEnter={() => {
                            if (leaveTimer.current) clearTimeout(leaveTimer.current)
                          }}
                          onMouseLeave={() => {
                            leaveTimer.current = setTimeout(() => {
                              setActiveDropdown(null)
                              leaveTimer.current = null
                            }, 200)
                          }}
                        >
                              <div className="px-6 pt-5 pb-3">
                                <h3 className="text-lg font-bold text-[#8B0000] mb-2">{activeLink.label}</h3>
                                <div className="w-full h-px bg-gray-200" />
                              </div>
                              <div
                                className="px-6 pb-5 grid gap-4"
                                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
                              >
                                {activeLink.children.map((child) =>
                                  child.disabled ? (
                                    <span
                                      key={child.label}
                                      className="block text-center px-3 py-4 text-[12px] font-medium text-gray-300 bg-gray-100/50 rounded-lg select-none cursor-not-allowed"
                                    >
                                      {child.label}
                                    </span>
                                  ) : (
                                    <Link
                                      key={child.label}
                                      href={child.href}
                                      target={child.external ? "_blank" : undefined}
                                      rel={child.external ? "noopener noreferrer" : undefined}
                                      className="block text-center px-3 py-4 text-[12.5px] font-semibold text-gray-800 bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg transition-colors"
                                    >
                                      {child.label}
                                    </Link>
                                  )
                                )}
                              </div>
                            </div>
                          </>
                        )
                      })()}
                </nav>

                {/* Desktop CTAs */}
                <div className="hidden xl:flex items-center gap-2">
                  <Link
                    href="https://3rddegreescreening.instascreen.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Login"
                    title="Login"
                    className="h-8 w-8 inline-flex items-center justify-center transition-colors rounded-xl text-foreground/65 hover:text-[#1A3FD8] hover:bg-[#1A3FD8]/5"
                  >
                    <UserRound className="h-[18px] w-[18px]" />
                  </Link>
                  <Link
                    href="/pricing"
                    className="text-[13.5px] h-8 px-4 inline-flex items-center font-medium rounded-xl border border-border text-foreground/80 transition-all hover:border-[#1A3FD8]/50 hover:text-[#1A3FD8] hover:bg-[#1A3FD8]/5"
                  >
                    Pricing
                  </Link>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="text-[13.5px] h-8 px-4 border font-medium transition-all border-border text-foreground/80 hover:border-[#1A3FD8]/50 hover:text-[#1A3FD8] hover:bg-[#1A3FD8]/5 rounded-xl"
                  >
                    <Link href={contactHref}>Contact Us</Link>
                  </Button>
                </div>

                {/* Mobile Hamburger */}
                <button
                  className="xl:hidden w-9 h-9 flex items-center justify-center rounded-xl transition-all text-foreground/70 bg-black/4 hover:bg-[#1A3FD8]/8 hover:text-[#1A3FD8]"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  aria-label="Toggle menu"
                >
                  <Menu className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>
          </header>
        </div>
      </div>

      {/* ── Desktop dropdown backdrop overlay ── */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-sm hidden xl:block transition-opacity duration-300",
          activeDropdown ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setActiveDropdown(null)}
      />

      {/* ── Mobile slide-over ── */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 backdrop-blur-sm xl:hidden transition-opacity duration-300",
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileOpen(false)}
      />

      {/* Drawer panel */}
      <div
        className={cn(
          "fixed top-0 right-0 bottom-0 z-50 w-full max-w-sm bg-[#050D1F] flex flex-col xl:hidden transition-transform duration-300 ease-out",
          mobileOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/8 flex-shrink-0">
          <Link href="/" onClick={() => setMobileOpen(false)}>
            <Image
              src="/images/logo-white.png"
              alt="3rd Degree Screening"
              width={140}
              height={44}
              className="h-8 w-auto object-contain"
            />
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/8 text-white/70 hover:bg-white/15 hover:text-white transition-all"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable nav */}
        <div className="flex-1 overflow-y-auto py-4 px-4">
          <nav className="space-y-0.5">
            {navLinks.map((link) => {
              const isOpen = openSection === link.label
              return (
                <div key={link.label}>
                  {link.children ? (
                    <>
                      <button
                        onClick={() => toggleSection(link.label)}
                        className="w-full flex items-center justify-between px-4 py-3.5 text-[15px] font-semibold text-white/85 hover:text-white hover:bg-white/6 rounded-xl transition-all"
                      >
                        {link.label}
                        <ChevronDown
                          className={cn(
                            "w-4 h-4 text-white/40 transition-transform duration-200",
                            isOpen ? "rotate-180 text-[#6CE2FF]" : ""
                          )}
                        />
                      </button>
                      <div
                        className={cn(
                          "overflow-hidden transition-all duration-300",
                          isOpen ? "opacity-100" : "opacity-0"
                        )}
                        style={{
                          maxHeight: isOpen
                            ? `${link.children.length * 48 + 72}px`
                            : "0px",
                        }}
                      >
                        <div className="mx-4 mb-2 pl-4 border-l border-white/10 space-y-0.5 pt-1">
                          {link.children.map((child) =>
                            child.disabled ? (
                              <span
                                key={child.label}
                                className="flex items-center gap-2.5 py-2.5 px-2 text-[13px] text-white/20 select-none cursor-not-allowed"
                              >
                                <ArrowRight className="w-3 h-3 text-white/10 flex-shrink-0" />
                                {child.label}
                              </span>
                            ) : (
                              <Link
                                key={child.label}
                                href={child.href}
                                onClick={() => setMobileOpen(false)}
                                target={child.external ? "_blank" : undefined}
                                rel={child.external ? "noopener noreferrer" : undefined}
                                className="flex items-center gap-2.5 py-2.5 px-2 text-[13px] text-white/65 hover:text-white rounded-lg transition-colors group"
                              >
                                <ArrowRight className="w-3 h-3 text-[#1A3FD8] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                                {child.label}
                              </Link>
                            )
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center px-4 py-3.5 text-[15px] font-semibold text-white/85 hover:text-white hover:bg-white/6 rounded-xl transition-all"
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>
        </div>

        {/* Drawer footer CTAs */}
        <div className="flex-shrink-0 px-5 py-6 border-t border-white/8 bg-[#030915]">
          <div className="grid grid-cols-2 gap-2">
            <Button asChild variant="outline" className="min-w-0 h-11 px-2 text-[14px] font-semibold border-white/30 text-white bg-white/10 shadow-[0_8px_24px_rgba(0,0,0,0.18)] hover:bg-white/16 hover:border-white/50 hover:text-white hover:shadow-[0_10px_28px_rgba(0,0,0,0.26)]">
              <Link href="https://3rddegreescreening.instascreen.net/" target="_blank" rel="noopener noreferrer">
                Login
              </Link>
            </Button>
            <Button asChild variant="outline" className="min-w-0 h-11 px-2 text-[14px] font-semibold border-white/30 text-white bg-white/10 shadow-[0_8px_24px_rgba(0,0,0,0.18)] hover:bg-white/16 hover:border-white/50 hover:text-white hover:shadow-[0_10px_28px_rgba(0,0,0,0.26)]">
              <Link href="/pricing">Pricing</Link>
            </Button>
            <Button asChild className="col-span-2 min-w-0 h-12 px-2 text-[14px] font-semibold border border-[#1A3FD8] bg-[#1A3FD8] text-white btn-cobalt-glow hover:bg-[#152FC8] hover:border-[#152FC8]">
              <Link href={contactHref}>Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
